import { applyMiddleware, createStore } from 'redux';
import { loadState, saveState } from 'localStorage';
import thunkMiddleware from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import { AUTH_STATE, AuthStateType, REPORT_TYPE_ENUM, ReportType } from 'appRedux/types/reduxTypes';
import { throttle } from 'lodash';
import { TypedUseSelectorHook, useSelector } from 'react-redux';

import {
  ICasinoSchema,
  ICasinoSchemaWithAccess,
  ICompareSlotInfo,
  IHistorySlotInfo,
  ILinkPlayerInfo,
  ISearchInfo,
} from './models/casinoModels';
import { historyInitialState } from './reducers/slotHistoryReducer';
import { favoritesInitialState } from './reducers/favoriteReducer';
import { IFavoritesState } from './models/favoritesModels';
import { playlistInitialState } from './reducers/playlistReducer';
import { IPlaylistState } from './models/playlistModels';
import { IDenomination, InsightValue, TimeFrameValue } from 'common/types';
import { filterInitialState } from './reducers/filtersReducer';
import { floormapInitialState } from './reducers/floormapReducer';
import { LatitudeLongitudeType } from './actions/floormapCommandCreator';
import { slotInitialState } from './reducers/slotReducer';
import rootReducer from './reducers';
import { slotCompareInitialState } from './reducers/slotCompareReducer';
import { OrderByEnum } from 'common/common';
import { SupportedPhoneCountryConfig } from 'common/phoneCountries';
import { IPlayerCardInfo } from 'services/auth';
import { setActiveInsightCasino } from 'services/insightCasino';
import { setActiveMachinesCatalog } from 'lib/machineCatalog';

export interface IUserInfo {
  user?: string;
  phone?: string;
  emailAddress?: string;
  level?: string;
  customer_id?: string;
  current_period_end?: number;
  canceled_at?: number;
  subscription_id?: string;
  appUserPk?: string;
  groups?: Array<string>;
  isProUser?: boolean;
  proCasinoId?: number;
  proCasinoSubscription?: boolean;
  hasCompletedOnboarding?: boolean;
  lastUpdateDateCollapse?: boolean;
  aliasInitial?: string;
  aliasInitial2?: string;
  aliasName?: string;
  firstName?: string;
  lastName?: string;
  isPublicVisible?: boolean;
  isCasinoAccount?: boolean;
  // Opt-in Explore slot-reel loading cards for this user (users.showLoadingReels,
  // from Login / GetMe). See lib/showLoadingReels.ts.
  showLoadingReels?: number | string | boolean | null;
  primaryCasino?: ICasinoSchemaWithAccess[];
  avatarId?: number;
  avatarBgId?: number;
}

export interface IReportInfo {
  id: ReportType;
}

export interface GeoJSONFeature {
  type: string;
  [key: string]: string | number;
}

export interface FloorPlan {
  kp_floor_plan_pk: number;
  floor_num: number;
  floor_name: string;
  kp_casino_fk: number;
  geojson: {
    type: string;
    features: GeoJSONFeature[];
  };
}

export interface SlotMetaData {
  game_name: string;
  slot_number: number;
  area: string;
  section: string;
  location: string;
}

export interface IOrientationInfo {
  isMobile: boolean;
  device: string;
  orientation: string;
  isLandscape: boolean;
  isOnlyPortrait: boolean;
}

export interface ICasinoSearchData {
  casinoId: number;
  data: ISearchInfo[];
}

export interface IDetailState {
  sections: string[];
}

export interface ITabProperties {
  timeframe: TimeFrameValue;
  insight: InsightValue;
  denominations: IDenomination[];
  advancedViewInsights: InsightValue[];
  orderby?: OrderByEnum;
}

export interface ITabGraphProperties {
  timeframe: TimeFrameValue;
  insight: InsightValue;
  denominations: IDenomination[];
  advancedViewInsights: InsightValue[];
  reportId?: number;
}

export interface IFilterState {
  leaderboard: ITabProperties;
  jackpot: ITabProperties;
  myplay: ITabProperties;
  graphfilter: ITabGraphProperties;
  mapfilter: ITabProperties;
  snapfilter: ITabProperties;
  main: ITabProperties;
  hottestSlots: ITabProperties;
}
export interface IFloormapState {
  heatFilter: number[];
  bearing: number;
  zoom: number;
  latitudeLongitude: LatitudeLongitudeType;
  pinLatitudeLongitude: LatitudeLongitudeType;
  pinZoom: number;
  pinSelected:boolean
}

export interface ISlotState {
  collapsed: boolean;
  sections: string[];
  selectedSlotsName: string;
  selectedSlots: string[];
  isBankFilter: boolean;
  isSearchMode: boolean;
  challengeEGMPinConfig: Record<string, { pinDisplayType: string; pinDisplayCode: string }> | null;
  challengeHeaderBgUrl: string | null;
  challengeName: string | null;
  challengePinDetails: {
    showInsightTopPins?: number | null;
    pinBackgroundColor?: string | null;
    pinNumberColor?: string | null;
  } | null;
}

export interface IPersistedState {
  userInfo?: IUserInfo;
  authState?: AuthStateType;
  supportedPhoneCountries?: SupportedPhoneCountryConfig[];
  emailAlertState?: boolean;
  reportType?: IReportInfo;
  casinoSchema?: ICasinoSchema;
  casinoEntrySeq?: number;
  orientationState?: IOrientationInfo;
  linkPlayerState?: ILinkPlayerInfo;
  historyState?: IHistorySlotInfo;
  casinoSearchData?: ICasinoSearchData;
  isSlotOrderAscending?: boolean;
  hasUserConfirm: boolean;
  favorites: IFavoritesState;
  playlist: IPlaylistState;
  filterState: IFilterState;
  floormapState: IFloormapState;
  slotState: ISlotState;
  compareSlotsState: ICompareSlotInfo;
  playerCardInfo?: IPlayerCardInfo | null;
}
export interface IState {
  app: {
    authJWK?: string;
    clientSecret?: string;
    InactivityAlertState?: boolean;
    persistedState: IPersistedState;
    linkPlayerState?: ILinkPlayerInfo;
    /** Non-persisted: GetApplicationConfigurations cache (loaded on app start). */
    applicationConfigurations?: import('lib/applicationConfig').ApplicationConfigRow[];
  };
}

const initialPersistedState: IPersistedState = {
  userInfo: { hasCompletedOnboarding: false, lastUpdateDateCollapse: false },
  authState: AUTH_STATE.SIGN_IN,
  emailAlertState: false,
  reportType: {
    id: REPORT_TYPE_ENUM.MAPS,
  },
  historyState: historyInitialState,
  casinoEntrySeq: 0,
  isSlotOrderAscending: true,
  linkPlayerState: {
    casinoLinkedAccounts: [],
    isPlayerLinked: false,
    linkedCasinoId: null,
  },
  hasUserConfirm: true,
  favorites: favoritesInitialState,
  playlist: playlistInitialState,
  filterState: filterInitialState,
  floormapState: floormapInitialState,
  slotState: slotInitialState,
  compareSlotsState: slotCompareInitialState,
};

const persistedState = loadState(initialPersistedState) || initialPersistedState;
// Hydrate machines catalog (GetSearchData) for display ids after persist rehydrate.
setActiveMachinesCatalog(persistedState.casinoSearchData?.data ?? null);
const initialState: IState = {
  app: {
    authJWK: '',
    InactivityAlertState: false,
    persistedState,
    applicationConfigurations: [],
  },
};

export default () => {
  const middlewares = [thunkMiddleware];
  const middlewareEnhancer = applyMiddleware(...middlewares);

  const enhancers = [middlewareEnhancer];
  const composedEnhancers = composeWithDevTools(...enhancers);

  const store = createStore(rootReducer, initialState, composedEnhancers);
  // Keep the casinoInsight router in sync with the selected casino object; it
  // switches on that object's insightInstance field only.
  let lastCasinoSchema = store.getState().app.persistedState.casinoSchema;
  setActiveInsightCasino(lastCasinoSchema);
  store.subscribe(() => {
    const next = store.getState().app.persistedState.casinoSchema;
    if (next !== lastCasinoSchema) {
      lastCasinoSchema = next;
      setActiveInsightCasino(next);
    }
  });
  store.subscribe(
    throttle(() => {
      saveState(store.getState().app.persistedState);
    }, 1000)
  );
  return store;
};

const appInitialState = initialState.app;
export { appInitialState };

export const useAppSelector: TypedUseSelectorHook<IState> = useSelector;
